import { ChangeEvent, useEffect, useState } from "react";

import {
  Autocomplete,
  InfoPanel,
  Select,
  SelectEvent,
  TextField,
} from "@king-ict/eupisi-ui/components";
import { CodeBook } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { CountySelect, FormCard } from "..";
import { DEFAULT_ADDRESS } from "./constants";
import { clearDependentFields } from "./utils";
import {
  searchHouseNumbers,
  searchMunicipalities,
  searchStreets,
  searchTowns,
} from "../../api/Geography/GeographyApi";
import { useDidUpdateEffect } from "../../hooks";
import {
  Address,
  GrandParentCodeBookPagedFilter,
  HierarchicalCodeBookPagedFilter,
} from "../../models";
import { useGetCountiesQuery } from "../../queries/codeBookQuery";
import { renderOibServiceInfoComponent } from "../../utils/rendering";
import { Placement } from "react-bootstrap/esm/types";

export interface AddressSelectProps {
  value?: Address | null;
  infoValue?: Address | null;
  title?: string;
  children?: any;
  readOnly?: boolean;
  disabled?: boolean;
  handleChange?: (value: Address) => void;
  errorText?: string;
  dropDownDirection?: Placement;
  canFlip?: boolean;
}

export const AddressSelect = ({
  value,
  infoValue,
  children,
  title,
  readOnly = true,
  disabled = false,
  errorText,
  dropDownDirection,
  canFlip,
  handleChange,
}: AddressSelectProps) => {
  const { t } = useTranslation();
  const { data: counties } = useGetCountiesQuery();
  const [municipalities, setMunicipalities] = useState<CodeBook[]>();
  const [towns, setTowns] = useState<CodeBook[]>();
  const [streets, setStreets] = useState<CodeBook[]>();
  const [houseNumbers, setHouseNumbers] = useState<CodeBook[]>();
  const [initialHouseNumbers, setInitialHouseNumbers] = useState<CodeBook[]>();
  const [address, setAddress] = useState<Address>(value ?? DEFAULT_ADDRESS);

  useDidUpdateEffect(() => {
    setAddress(value ?? DEFAULT_ADDRESS);
    setHouseNumbers(initialHouseNumbers);
  }, [value]);

  useEffect(() => {
    (async () => {
      if (address?.countyId && (disabled || readOnly)) {
        setMunicipalities([
          { id: address?.municipalityId ?? 0, name: address?.municipality },
        ]);
      } else if (address?.countyId && !readOnly && !disabled) {
        const search: HierarchicalCodeBookPagedFilter = {
          pageSize: 1500,
          page: 1,
          parentId: [address.countyId],
          search: "",
        };
        const result = await searchMunicipalities(search);
        setMunicipalities(result.results);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address.countyId, disabled, readOnly]);

  useEffect(() => {
    (async () => {
      if (address?.municipalityId && (disabled || readOnly)) {
        setTowns([{ id: address?.townId ?? 0, name: address?.town }]);
      } else if (address?.municipalityId && !readOnly && !disabled) {
        const searchTown: GrandParentCodeBookPagedFilter = {
          pageSize: 1500,
          page: 1,
          ids: [],
          parentId: address?.municipalityId ? [address?.municipalityId] : [],
          search: "",
        };
        const result = await searchTowns(searchTown);
        setTowns(result.results);
      } else setTowns(undefined);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address?.municipalityId, disabled, readOnly]);

  useEffect(() => {
    (async () => {
      if (address?.townId && (disabled || readOnly)) {
        setStreets([{ id: address?.streetId ?? 0, name: address?.street }]);
      } else if (address?.townId && !readOnly && !disabled) {
        const searchTown: GrandParentCodeBookPagedFilter = {
          pageSize: 4500,
          page: 1,
          ids: [],
          parentId: address?.townId ? [address?.townId] : [],
          search: "",
        };
        const result = await searchStreets(searchTown);
        setStreets(result.results);
      } else setStreets(undefined);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address?.townId, disabled, readOnly]);

  useEffect(() => {
    (async () => {
      if (address?.townId && (disabled || readOnly)) {
        setHouseNumbers([
          { id: address?.houseNumberId ?? 0, name: address?.houseNumber ?? "" },
        ]);
      } else if (address?.streetId && !readOnly && !disabled) {
        const searchTown: HierarchicalCodeBookPagedFilter = {
          pageSize: 2500,
          page: 1,
          ids: [],
          parentId: address?.streetId ? [address?.streetId] : [],
          search: "",
        };
        const result = await searchHouseNumbers(searchTown);
        setHouseNumbers(result.results);
        setInitialHouseNumbers(result.results);
      } else {
        setHouseNumbers(undefined);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address?.streetId, disabled, readOnly]);

  const handleCountyChange = async (e: ChangeEvent<SelectEvent>) => {
    const countyId = Number(e.target.value);
    const county = counties?.find((e) => e.id === countyId)?.name ?? "";

    const newAddress: Address = {
      ...DEFAULT_ADDRESS,
      countyId,
      county,
      ...clearDependentFields(address, "county"),
    };
    setAddress(newAddress);
    handleChange?.(newAddress);
  };

  const handleMunicipalityChange = async (e: ChangeEvent<SelectEvent>) => {
    const municipalityId = Number(e.target.value);
    const municipality =
      municipalities?.find((e) => e.id === municipalityId)?.name ?? "";

    const newAddress: Address = {
      ...DEFAULT_ADDRESS,
      ...address,
      municipalityId,
      municipality,
      ...clearDependentFields(address, "municipality"),
    };
    setAddress(newAddress);
    handleChange?.(newAddress);
  };

  const handleTownChange = async (e: ChangeEvent<SelectEvent>) => {
    const townId = Number(e.target.value);
    const town = towns?.find((e) => e.id === townId)?.name ?? "";

    const newAddress: Address = {
      ...DEFAULT_ADDRESS,
      ...address,
      townId,
      town,
      ...clearDependentFields(address, "town"),
    };
    setAddress(newAddress);
    handleChange?.(newAddress);
  };

  const handleStreetChange = async (e: ChangeEvent<SelectEvent>) => {
    const streetId = Number(e.target.value);
    const street = streets?.find((e) => e.id === streetId)?.name ?? "";

    const newAddress: Address = {
      ...address,
      streetId,
      street,
      ...clearDependentFields(address, "street"),
    };
    setAddress(newAddress);
    handleChange?.(newAddress);
  };

  const handleHouseNumberChange = async (e: ChangeEvent<SelectEvent>) => {
    const houseNumberId = Number(e.target.value);
    const houseNumber =
      houseNumbers?.find((e) => e.id === houseNumberId)?.name ?? "";

    if (!houseNumberId && initialHouseNumbers) {
      setHouseNumbers(initialHouseNumbers);
    }

    const newAddress: Address = {
      ...address,
      houseNumberId: houseNumberId ? houseNumberId : undefined,
      houseNumber: houseNumber ? houseNumber : null,
    };
    setAddress(newAddress);
    handleChange?.(newAddress);
  };

  const isEmptyInfo = infoValue === null;
  const shouldShowServiceInfo = infoValue || isEmptyInfo;

  return (
    <FormCard cardTitle={title}>
      <Row>
        <Col md="4">
          {readOnly ? (
            <TextField
              label={t("common.schoolCandidateModel.countyList")}
              value={address?.county}
              readonly
              plaintext
            />
          ) : (
            <CountySelect
              label={t("common.schoolCandidateModel.countyList")}
              name={"CountyId"}
              value={address?.countyId}
              onChange={(e) => handleCountyChange(e)}
              disabled={disabled}
              errorText={errorText && "-"}
              dropDownDirection={dropDownDirection}
              canFlip={canFlip}
            />
          )}
          {shouldShowServiceInfo &&
            renderOibServiceInfoComponent({
              message: infoValue?.county,
              clickHandler: () => {
                setAddress(infoValue || address);
                handleChange?.(address);
              },
              currentValue: address?.county,
              oibValue: infoValue?.county,
            })}
        </Col>
        <Col md="4">
          {readOnly ? (
            <TextField
              label={t("common.schoolCandidateModel.municipalityList")}
              value={address?.municipality}
              readonly
              plaintext
              errorText={errorText}
            />
          ) : (
            <Autocomplete
              label={t("common.schoolCandidateModel.municipalityList")}
              name={"MunicipalityId"}
              value={address?.municipalityId}
              selectData={codeBookToSelectData(municipalities)}
              onChange={(e) => handleMunicipalityChange(e)}
              disabled={disabled || !municipalities}
              errorText={errorText && "-"}
              dropdownDirection={dropDownDirection}
              canFlip={canFlip}
            />
          )}
          {shouldShowServiceInfo &&
            renderOibServiceInfoComponent({
              message: infoValue?.municipality,
              clickHandler: () => {
                setAddress(infoValue || address);
                handleChange?.(address);
              },
              currentValue: address?.municipality,
              oibValue: infoValue?.municipality,
            })}
        </Col>
        <Col md="4">
          {readOnly ? (
            <TextField
              label={t("common.schoolCandidateModel.townList")}
              value={address?.town}
              readonly
              plaintext
              errorText={errorText}
            />
          ) : (
            <Autocomplete
              label={t("common.schoolCandidateModel.townList")}
              name={"TownId"}
              value={address?.townId}
              selectData={codeBookToSelectData(towns)}
              onChange={(e) => handleTownChange(e)}
              disabled={disabled || !towns}
              errorText={errorText && "-"}
              dropdownDirection={dropDownDirection}
              canFlip={canFlip}
            />
          )}
          {shouldShowServiceInfo &&
            renderOibServiceInfoComponent({
              message: infoValue?.town,
              clickHandler: () => {
                setAddress(infoValue || address);
                handleChange?.(address);
              },
              currentValue: address?.town,
              oibValue: infoValue?.town,
            })}
        </Col>
      </Row>
      <Row>
        <Col md="4">
          {readOnly ? (
            <TextField
              label={t("common.schoolCandidateModel.streetList")}
              value={address?.street}
              readonly
              plaintext
              errorText={errorText}
            />
          ) : (
            <Autocomplete
              label={t("common.schoolCandidateModel.streetList")}
              name={"StreetId"}
              value={address?.streetId ?? undefined}
              selectData={codeBookToSelectData(streets)}
              onChange={(e) => handleStreetChange(e)}
              disabled={disabled || !streets}
              errorText={errorText && "-"}
              dropdownDirection={dropDownDirection}
              canFlip={canFlip}
            />
          )}
          {shouldShowServiceInfo &&
            renderOibServiceInfoComponent({
              message: infoValue?.street,
              clickHandler: () => {
                setAddress(infoValue || address);
                handleChange?.(address);
              },
              currentValue: address?.street,
              oibValue: infoValue?.street,
            })}
        </Col>
        <Col md="4">
          {readOnly ? (
            <TextField
              label={t("common.schoolCandidateModel.houseNumberSelector")}
              value={address?.houseNumber ?? ""}
              readonly
              plaintext
              errorText={errorText}
            />
          ) : (
            <Select
              label={t("common.schoolCandidateModel.houseNumberSelector")}
              name={"HouseNumberId"}
              value={address?.houseNumberId ?? undefined}
              selectData={codeBookToSelectData(houseNumbers)}
              onChange={(e) => handleHouseNumberChange(e)}
              disabled={disabled || !houseNumbers}
              errorText={errorText && "-"}
              clearable
            />
          )}
          {shouldShowServiceInfo &&
            renderOibServiceInfoComponent({
              message: infoValue?.houseNumber,
              clickHandler: () => {
                setAddress(infoValue || address);
                handleChange?.(address);
              },
              currentValue: address?.houseNumber,
              oibValue: infoValue?.houseNumber,
            })}
        </Col>
        <Col md="4">
          <br />
          {!errorText || <InfoPanel color="danger" message={errorText ?? ""} />}
        </Col>
      </Row>
      {children}
    </FormCard>
  );
};
